import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Mail, Phone, MapPin, ArrowUpRight, Navigation } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";
import { KIKI } from "@/constants/testIds";
import { useT, useLang } from "@/lib/i18n";

export function Contact({ onEnquire }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const t = useT();
  const { lang } = useLang();
  const isHi = lang === "hi";

  const subscribe = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) return toast.error(t("contact.newsInvalid"));
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email });
      if (data.already) toast.info(t("contact.newsAlready"));
      else {
        toast.success(t("contact.newsSuccess"));
        track("newsletter_subscribed", { email });
      }
      setEmail("");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid={KIKI.contact} className="relative py-16 md:py-24 bg-[#0A0A0A] text-white overflow-hidden">
      <div className="max-w-[90rem] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-8">
            <div className="flex items-baseline gap-4 mb-6">
              <span className="font-mono text-sm text-white/50">05</span>
              <span className={`label-overline text-white/60 ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>{t("contact.overline")}</span>
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
              className={`font-black text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight ${isHi ? "font-hindi" : "font-sans"}`}
            >
              {t("contact.titleA")}<span className={`text-[#FFCC00] ${isHi ? "font-hindi" : "font-display italic font-normal"}`}>{t("contact.titleAItalic")}</span><br />
              {t("contact.titleB")}<span className={isHi ? "font-hindi" : "font-display italic font-normal"}>{t("contact.titleBItalic")}</span>
            </motion.h2>
            <p className={`mt-8 max-w-xl text-white/70 text-base md:text-lg leading-relaxed ${isHi ? "font-hindi" : ""}`}>
              {t("contact.lead")}
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 flex md:justify-end md:items-end">
            <button
              onClick={onEnquire}
              data-testid={KIKI.ctaBookSeat}
              className={`btn-pill-yellow text-base px-6 py-4 ${isHi ? "font-hindi" : ""}`}
            >
              {t("contact.cta")}
              <ArrowUpRight className="w-5 h-5" strokeWidth={2} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-10 border-t border-white/15 pt-14">
          {/* Registration centre */}
          <div className="col-span-12 md:col-span-4">
            <div className={`label-overline text-white/50 mb-6 ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>{t("contact.regLabel")}</div>
            <div className="space-y-5 text-sm">
              <div className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-1 text-[#FFCC00]" strokeWidth={1.8} />
                <div className="space-y-1">
                  <a href="tel:+918800288994" className="link-sweep block font-semibold text-white">+91 88002 88994</a>
                  <a href="tel:+919717283555" className="link-sweep block text-white/70">+91 97172 83555</a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-1 text-[#FFCC00]" strokeWidth={1.8} />
                <a href="mailto:info@kikicentre.com" className="link-sweep text-white">info@kikicentre.com</a>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 text-[#FFCC00]" strokeWidth={1.8} />
                <div className={`text-white/70 leading-relaxed ${isHi ? "font-hindi" : ""}`}>
                  {t("contact.addr.line1")}<br />
                  {t("contact.addr.line2")}<br />
                  {t("contact.addr.line3")}<br />
                  {t("contact.addr.line4")}
                </div>
              </div>
            </div>

            {/* Map embed */}
            <div className="mt-8" data-testid="contact-map">
              <div className={`label-overline text-white/50 mb-3 ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>{t("contact.mapLabel")}</div>
              <div className="relative rounded-2xl overflow-hidden border border-white/15 bg-black/60 group">
                <div className="absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-black/50 to-transparent z-[1] pointer-events-none" />
                <iframe
                  title="KIKI Centre for Technology — Sector 75A, Gurugram"
                  src="https://maps.google.com/maps?q=1%2F47%2C%20Sector%2075A%2C%20Gurugram%2C%20Haryana%20122012&hl=en&z=15&output=embed"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full h-56 md:h-64 border-0 grayscale-[0.15] group-hover:grayscale-0 transition-[filter] duration-700"
                  allowFullScreen
                />
                <span className="absolute top-3 left-3 z-[2] inline-flex items-center gap-2 bg-[#FFCC00] text-black px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.16em] uppercase shadow-sm">
                  <MapPin className="w-3 h-3" strokeWidth={2.4} /> KIKI · 75A
                </span>
              </div>
              <a
                data-testid="contact-directions-link"
                href="https://www.google.com/maps/dir/?api=1&destination=1%2F47%2C%20Sector%2075A%2C%20Gurugram%2C%20Haryana%20122012"
                target="_blank"
                rel="noopener noreferrer"
                className={`mt-4 inline-flex items-center gap-2 bg-[#FFCC00] text-black rounded-full px-4 py-2.5 text-[13px] font-semibold group ${isHi ? "font-hindi" : ""}`}
              >
                <Navigation className="w-4 h-4" strokeWidth={2} />
                {t("contact.mapDirections")}
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
              </a>
            </div>
          </div>

          {/* Newsletter */}
          <div className="col-span-12 md:col-span-5">
            <div className={`label-overline text-white/50 mb-6 ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>{t("contact.newsLabel")}</div>
            <h3 className={`font-extrabold text-2xl md:text-3xl leading-tight mb-6 max-w-md ${isHi ? "font-hindi" : "font-sans"}`}>
              {t("contact.newsTitle")}
            </h3>
            <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3 max-w-md">
              <input
                data-testid={KIKI.newsletterInput}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("contact.newsPlaceholder")}
                className="flex-1 px-5 py-3.5 rounded-full bg-white/10 border border-white/15 focus:border-[#FFCC00] focus:outline-none text-white placeholder:text-white/40"
              />
              <button
                data-testid={KIKI.newsletterSubmit}
                type="submit"
                disabled={loading}
                className={`btn-pill-yellow px-6 ${isHi ? "font-hindi" : ""}`}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("contact.newsSubmit")}
              </button>
            </form>
          </div>

          {/* Commitment */}
          <div className="col-span-12 md:col-span-3 md:text-right">
            <div className={`label-overline text-white/50 mb-6 ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>{t("contact.commitLabel")}</div>
            <div className="space-y-3 text-sm text-white/85">
              <div className="inline-flex items-center gap-2 bg-white/8 border border-white/10 rounded-full px-4 py-2">
                <span className="w-6 h-6 grid place-items-center rounded-full bg-[#FFCC00] text-black text-xs font-bold">✓</span>
                <span className={isHi ? "font-hindi" : ""}>{t("contact.commit1")}</span>
              </div>
              <br />
              <div className="inline-flex items-center gap-2 bg-white/8 border border-white/10 rounded-full px-4 py-2">
                <span className="w-6 h-6 grid place-items-center rounded-full bg-[#FFCC00] text-black text-xs font-bold">✓</span>
                <span className={isHi ? "font-hindi" : ""}>{t("contact.commit2")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
